/** Build scene + per-block audio lanes for the editor timeline UI. */

const SKIP_TYPES = new Set(['scene_transition', 'code_highlight', 'flowchart_arrow']);

const ROLE_LABELS = {
  page_intro: 'Intro',
  continuity: 'Bridge',
  term: 'Term',
  gloss: 'Gloss',
  visual: 'Diagram',
};

const TYPE_LABELS = {
  scene_title: 'Intro',
  bullet: 'Term',
  explanation: 'Gloss',
  image: 'Diagram',
  keyword: 'Keyword',
  code_step: 'Code line',
  code_highlight: 'Highlight',
  console_output: 'Console',
  flowchart_node: 'Flowchart',
  flowchart_arrow: 'Arrow',
};

export function blockAudioLabel(block) {
  const role = (block?.segment_role || '').trim();
  if (role && ROLE_LABELS[role]) return ROLE_LABELS[role];
  const type = (block?.type || '').trim();
  return TYPE_LABELS[type] || type || 'Block';
}

export function blockLaneColor(block) {
  const role = (block?.segment_role || '').trim();
  const type = (block?.type || '').trim();
  if (role === 'visual' || type === 'image') {
    return 'bg-amber-600/80 border-amber-400/70 text-amber-50';
  }
  if (role === 'page_intro' || type === 'scene_title') {
    return 'bg-emerald-600/80 border-emerald-400/70 text-emerald-50';
  }
  if (role === 'term' || type === 'bullet') {
    return 'bg-sky-600/80 border-sky-400/70 text-sky-50';
  }
  if (role === 'gloss' || type === 'explanation') {
    return 'bg-violet-600/80 border-violet-400/70 text-violet-50';
  }
  if (type === 'code_step' || type === 'console_output') {
    return 'bg-slate-600/80 border-slate-400/70 text-slate-100';
  }
  return 'bg-slate-600/70 border-slate-500/60 text-slate-100';
}

function hasSpokenAudio(block) {
  const type = (block?.type || '').trim();
  if (SKIP_TYPES.has(type)) return false;
  const narr = String(block?.narration || '').trim();
  return narr.length > 0;
}

function pct(time, duration) {
  if (!duration || duration <= 0) return 0;
  return Math.max(0, Math.min(100, (time / duration) * 100));
}

/**
 * @param {object[]} segments - editor segments from API
 * @param {number} duration - total timeline seconds
 * @returns {{ duration: number, scenes: object[] }}
 */
export function buildSceneAudioTimeline(segments, duration) {
  const total = Math.max(0.001, Number(duration) || 0.001);
  const scenes = (segments || []).map((seg, sceneIndex) => {
    const sceneStart = Number(seg.start_time) || 0;
    const sceneEnd = Number(seg.end_time) || sceneStart;
    const sceneDur = Math.max(0.001, sceneEnd - sceneStart);

    const blocks = (seg.blocks || [])
      .filter(hasSpokenAudio)
      .map((b, i) => ({
        ...b,
        _sort: b.segment_index != null ? Number(b.segment_index) : i,
      }))
      .sort((a, b) => a._sort - b._sort)
      .map((b, blockIndex) => {
        const start = Number(b.narration_start ?? b.start ?? sceneStart);
        const end = Number(b.narration_end ?? b.end ?? start + 0.5);
        const audioDur = Number(b.audio_duration) || Math.max(0.1, end - start);
        return {
          blockId: b.block_id,
          blockIndex,
          type: b.type,
          segmentRole: b.segment_role,
          label: blockAudioLabel(b),
          colorClass: blockLaneColor(b),
          start,
          end,
          duration: audioDur,
          leftPct: pct(start, total),
          widthPct: Math.max(0.4, pct(end, total) - pct(start, total)),
          narrationPreview: String(b.narration || '').slice(0, 72),
          topic: b.slide_context?.topic,
        };
      });

    const lastNarrEnd = blocks.length
      ? Math.max(...blocks.map((b) => b.end))
      : sceneStart;
    const gap =
      sceneEnd > lastNarrEnd + 0.04
        ? {
            start: lastNarrEnd,
            end: sceneEnd,
            duration: sceneEnd - lastNarrEnd,
            leftPct: pct(lastNarrEnd, total),
            widthPct: Math.max(0.3, pct(sceneEnd, total) - pct(lastNarrEnd, total)),
          }
        : null;

    return {
      sceneIndex,
      id: seg.id,
      title: seg.title || `Scene ${sceneIndex + 1}`,
      start: sceneStart,
      end: sceneEnd,
      duration: sceneDur,
      leftPct: pct(sceneStart, total),
      widthPct: Math.max(0.5, pct(sceneEnd, total) - pct(sceneStart, total)),
      blocks,
      gap,
      blockCount: blocks.length,
    };
  });

  return { duration: total, scenes };
}

/** Which scene / block is active at playhead. */
export function activeAtPlayhead(timeline, playhead) {
  const t = Number(playhead) || 0;
  let sceneIndex = -1;
  let blockIndex = -1;

  for (const sc of timeline.scenes || []) {
    if (t >= sc.start && t < sc.end) sceneIndex = sc.sceneIndex;
    for (const b of sc.blocks || []) {
      if (t >= b.start && t < b.end) {
        sceneIndex = sc.sceneIndex;
        blockIndex = b.blockIndex;
      }
    }
  }
  return { sceneIndex, blockIndex };
}
